import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link as MuiLink,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import InsertDriveFileOutlined from '@material-ui/icons/InsertDriveFileOutlined';
import FolderOpen from '@material-ui/icons/FolderOpen';
import EmptyState from 'components/shared/EmptyState';
import Errors from 'components/shared/Errors';
import Success from 'components/shared/Success';
import useHostFiles from 'modules/hostFiles/hooks/useHostFiles';
import FileThumbnail from 'modules/hostFiles/components/FileThumbnail';
import MultiUploadDialog from 'modules/hostFiles/components/MultiUploadDialog';
import {
  getCottageName,
  getMimeLabel,
  formatFileSize,
  isImageMime,
  getThumbnailUrl,
  sortFilesByName,
  sortCottagesByName,
  DEFAULT_MAX_FILES_PER_SLOT,
} from 'modules/hostFiles/utils';
import PATHS from 'constants/paths';

const useStyles = makeStyles((theme) => ({
  listItem: {
    borderRadius: 6,
    '&.Mui-selected': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  subtitle: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  switchLink: {
    cursor: 'pointer',
    marginLeft: theme.spacing(1),
  },
  pickerList: {
    paddingBottom: theme.spacing(2),
  },
  pickerItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  pickerCount: {
    color: theme.palette.text.secondary,
  },
  actionButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  manageLink: {
    cursor: 'pointer',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
}));

const HostFileBrowserDialog = ({ open, onClose, onSelect, cottageId }) => {
  const classes = useStyles();
  const intl = useIntl();
  const history = useHistory();
  const hf = useHostFiles();

  const [selectedId, setSelectedId] = React.useState(null);
  const [activeCottageId, setActiveCottageId] = React.useState(cottageId);
  const [showPicker, setShowPicker] = React.useState(false);
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const uploadDialogRef = React.useRef(null);
  const handleUploadOpen = React.useCallback(() => {
    setUploadOpen(true);
    uploadDialogRef.current?.openPicker();
  }, []);

  React.useEffect(() => {
    if (open) {
      setSelectedId(null);
      setActiveCottageId(cottageId);
      setShowPicker(false);
      setUploadOpen(false);
    }
  }, [open, cottageId]);

  const cottageNameMap = React.useMemo(() => {
    const map = {};
    hf.cottages.forEach((c) => {
      map[c.id] = getCottageName(c);
    });
    return map;
  }, [hf.cottages]);

  const fileCounts = React.useMemo(() => {
    const fc = {};
    (hf.files || []).forEach((file) => {
      (file.listing_ids || []).forEach((lid) => {
        fc[lid] = (fc[lid] || 0) + 1;
      });
    });
    return fc;
  }, [hf.files]);

  const filteredFiles = React.useMemo(() => {
    if (!hf.files) return [];
    if (activeCottageId) {
      // Include files with empty listing_ids so unassigned files (if any
      // ever exist in BE state) remain reachable from a cottage view.
      return sortFilesByName(
        hf.files.filter((f) => {
          const ids = f.listing_ids || [];
          return ids.length === 0 || ids.includes(activeCottageId);
        }),
      );
    }
    return sortFilesByName(hf.files);
  }, [hf.files, activeCottageId]);

  const activeCottageName = cottageNameMap[activeCottageId] || '';

  const handleConfirm = () => {
    if (selectedId) {
      onSelect(selectedId);
    }
  };

  const handleGoToMyFiles = () => {
    onClose();
    history.push(PATHS.hostFiles.root);
  };

  const handlePickCottage = (id) => {
    setActiveCottageId(id);
    setSelectedId(null);
    setShowPicker(false);
  };

  const hasFiles = filteredFiles.length > 0;
  const maxPerSlot = hf.config?.max_files_per_slot ?? DEFAULT_MAX_FILES_PER_SLOT;
  const uploadCottage = activeCottageId ? hf.cottages.find((c) => c.id === activeCottageId) : null;
  const cottageMissing = activeCottageId != null && !uploadCottage;
  const hasAnyCottages = hf.cottages.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {showPicker
          ? intl.formatMessage({
              id: 'conversations.fileBrowserPickListing',
              defaultMessage: 'Choose listing',
            })
          : intl.formatMessage({
              id: 'conversations.fileBrowserTitle',
              defaultMessage: 'Choose file',
            })}
      </DialogTitle>
      <DialogContent>
        {!showPicker && activeCottageId && !cottageMissing && (
          <Typography variant="body2" className={classes.subtitle}>
            {intl.formatMessage(
              { id: 'conversations.fileBrowserFrom', defaultMessage: 'From: {name}' },
              { name: activeCottageName },
            )}
            <MuiLink
              component="button"
              type="button"
              className={classes.switchLink}
              color="primary"
              onClick={() => setShowPicker(true)}
            >
              {intl.formatMessage({
                id: 'conversations.fileBrowserSwitch',
                defaultMessage: 'Switch',
              })}
            </MuiLink>
          </Typography>
        )}

        {showPicker ? (
          <List disablePadding className={classes.pickerList}>
            {sortCottagesByName(hf.cottages).map((c) => (
              <ListItem
                key={c.id}
                button
                className={classes.pickerItem}
                onClick={() => handlePickCottage(c.id)}
              >
                <ListItemIcon>
                  <FolderOpen />
                </ListItemIcon>
                <ListItemText primary={`#${c.id} - ${cottageNameMap[c.id]}`} />
                <Typography variant="body2" className={classes.pickerCount}>
                  ({fileCounts[c.id] || 0})
                </Typography>
              </ListItem>
            ))}
          </List>
        ) : (
          <>
            {hf.isLoading && (
              <div className={classes.loading}>
                <CircularProgress />
              </div>
            )}
            {!hf.isLoading && !hasFiles && cottageMissing && hasAnyCottages && (
              <EmptyState
                icon={FolderOpen}
                title={intl.formatMessage({
                  id: 'conversations.fileBrowserMissingCottageTitle',
                  defaultMessage: 'Listing not available',
                })}
                description={intl.formatMessage({
                  id: 'conversations.fileBrowserMissingCottageDescription',
                  defaultMessage:
                    'Files for this listing cannot be shown. Choose another listing to see its files.',
                })}
              >
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.actionButton}
                  onClick={() => setShowPicker(true)}
                >
                  {intl.formatMessage({
                    id: 'conversations.fileBrowserSwitchCottage',
                    defaultMessage: 'Choose listing',
                  })}
                </Button>
                <br />
                <MuiLink
                  component="button"
                  type="button"
                  className={classes.manageLink}
                  color="textSecondary"
                  onClick={handleGoToMyFiles}
                >
                  {intl.formatMessage({
                    id: 'conversations.fileBrowserManageFiles',
                    defaultMessage: 'Manage My files',
                  })}
                </MuiLink>
              </EmptyState>
            )}
            {!hf.isLoading && !hasFiles && cottageMissing && !hasAnyCottages && (
              <EmptyState
                icon={FolderOpen}
                title={intl.formatMessage({
                  id: 'conversations.fileBrowserNoCottagesTitle',
                  defaultMessage: 'No listings available',
                })}
                description={intl.formatMessage({
                  id: 'conversations.fileBrowserNoCottagesDescription',
                  defaultMessage: 'You have no listings with saved files.',
                })}
              >
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.actionButton}
                  onClick={handleGoToMyFiles}
                >
                  {intl.formatMessage({
                    id: 'conversations.fileBrowserManageFiles',
                    defaultMessage: 'Manage My files',
                  })}
                </Button>
              </EmptyState>
            )}
            {!hf.isLoading && !hasFiles && !cottageMissing && (
              <EmptyState
                icon={InsertDriveFileOutlined}
                title={intl.formatMessage({
                  id: 'conversations.fileBrowserNoFilesTitle',
                  defaultMessage: 'No saved files yet',
                })}
                description={intl.formatMessage({
                  id: 'conversations.fileBrowserNoFilesDescription',
                  defaultMessage: 'Upload a file to save it in My files and reuse it.',
                })}
              >
                {uploadCottage && (
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.actionButton}
                    onClick={handleUploadOpen}
                  >
                    {intl.formatMessage({
                      id: 'hostFiles.upload',
                      defaultMessage: 'Upload file',
                    })}
                  </Button>
                )}
                <br />
                <MuiLink
                  component="button"
                  type="button"
                  className={classes.manageLink}
                  color="textSecondary"
                  onClick={handleGoToMyFiles}
                >
                  {intl.formatMessage({
                    id: 'conversations.fileBrowserManageFiles',
                    defaultMessage: 'Manage My files',
                  })}
                </MuiLink>
              </EmptyState>
            )}
            {!hf.isLoading && hasFiles && (
              <List disablePadding>
                {filteredFiles.map((file) => {
                  const isImage = isImageMime(file.mime_type);
                  return (
                    <ListItem
                      key={file.id}
                      button
                      selected={selectedId === file.id}
                      onClick={() => setSelectedId(file.id)}
                      className={classes.listItem}
                    >
                      <ListItemIcon>
                        <FileThumbnail
                          mimeType={file.mime_type}
                          src={isImage ? getThumbnailUrl(file.url) : undefined}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={file.display_name || file.filename}
                        secondary={
                          <span className={classes.meta}>
                            {getMimeLabel(file.mime_type)}
                            {file.size_bytes ? ` - ${formatFileSize(file.size_bytes)}` : ''}
                          </span>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </>
        )}
      </DialogContent>
      {!showPicker && (
        <DialogActions>
          <Button onClick={handleGoToMyFiles} style={{ marginRight: 'auto' }}>
            {intl.formatMessage({
              id: 'conversations.goToMyFiles',
              defaultMessage: 'My files',
            })}
          </Button>
          <Button onClick={onClose}>
            {intl.formatMessage({ id: 'Cancel', defaultMessage: 'Cancel' })}
          </Button>
          <Button onClick={handleConfirm} color="primary" disabled={!selectedId}>
            {intl.formatMessage({
              id: 'conversations.fileBrowserAttach',
              defaultMessage: 'Attach',
            })}
          </Button>
        </DialogActions>
      )}

      {uploadCottage && (
        <MultiUploadDialog
          ref={uploadDialogRef}
          open={uploadOpen}
          config={hf.config}
          cottages={[uploadCottage]}
          counts={hf.counts}
          maxCount={maxPerSlot}
          isUploading={hf.isUploading}
          uploadProgress={hf.uploadProgress}
          uploadFileIndex={hf.uploadFileIndex}
          uploadFileCount={hf.uploadFileCount}
          onUpload={hf.onUpload}
          onClose={() => setUploadOpen(false)}
        />
      )}
      <Success success={hf.success} onClose={hf.onClearStatus} autoHideDuration={5000} />
      <Errors errors={hf.error} onClose={hf.onClearStatus} autoHideMs={5000} />
    </Dialog>
  );
};

HostFileBrowserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  cottageId: PropTypes.number,
};

export default HostFileBrowserDialog;
